import {
  Box,
  Button,
  ButtonGroup,
  ButtonVariants,
  Divider,
  FormControlSizes,
  IconName,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  Text,
  ToggleButton,
  Tooltip,
  useMixpanel,
  usePortal,
} from '@talos/kyoko';
import { useFeatureFlag, useNavigateToMultilegCombo } from '../../../../hooks';
import { MultilegComboType } from '../../../MultilegCombo/enums';
import { getIsPriceGridFavoritesOnly } from '../../favorites';
import { PriceGridSymbolFilters, usePriceGridFilterControls } from '../../filters';
import type { PriceGridLadderType } from '../../ladder';
import { PriceGridFavoriteFilterButton } from '../../PriceGridFavoriteFilterButton';
import { PriceGridHeaderActions, PriceGridHeaderContainer, PriceGridHeaderFilters } from '../../PriceGridHeaderLayout';
import { PriceGridLadderControls } from '../../PriceGridLadderControls';
import { PriceGridPrimingControls } from '../../PriceGridPrimingControls';
import type { PriceGridFilterState, PriceGridHeaderParams } from '../../types';
import { getSelectedMultilegTypes, MULTILEG_PRICE_GRID_TYPE_OPTIONS, type MultilegPriceGridType } from './filters';

function areSelectionsEqual(a: readonly unknown[] | undefined, b: readonly unknown[] | undefined) {
  const aSelections = a ?? [];
  const bSelections = b ?? [];
  return aSelections.length === bSelections.length && aSelections.every((value, index) => value === bSelections[index]);
}

interface MultilegPriceGridHeaderProps extends PriceGridHeaderParams {
  filterState: PriceGridFilterState;
  ladderType: PriceGridLadderType;
  minPriceIncrement: string;
  onFilterStateChanged: (filterState: PriceGridFilterState) => void;
  onLadderTypeChange: (ladderType: PriceGridLadderType) => void;
  onPriceIncrementChange: (increment: string) => void;
  priceIncrement: string;
}

export function MultilegPriceGridHeader({
  filtersPortalId,
  filterState,
  ladderType,
  minPriceIncrement,
  onFilterStateChanged,
  onLadderTypeChange,
  onPriceIncrementChange,
  priceIncrement,
}: MultilegPriceGridHeaderProps) {
  const mixpanel = useMixpanel(MixpanelEventSource.MultilegPriceGrid);
  const { setPortalRef: filtersPortalRef } = usePortal(filtersPortalId);
  const navigateToMultilegCombo = useNavigateToMultilegCombo();
  const { showMultiLegSecmaster } = useFeatureFlag();
  const selectedMultilegTypes = getSelectedMultilegTypes(filterState);
  const { orderPresetID, subAccount } = filterState;

  const handleFilterStateChanged = (nextFilterState: PriceGridFilterState) => {
    if (getIsPriceGridFavoritesOnly(filterState) !== getIsPriceGridFavoritesOnly(nextFilterState)) {
      mixpanel.track(MixpanelEvent.FavoriteFilter, {
        [MixpanelEventProperty.Enabled]: getIsPriceGridFavoritesOnly(nextFilterState),
        [MixpanelEventProperty.Source]: MixpanelEventSource.MultilegPriceGrid,
      });
    }

    const trackSelectionFilter = (filter: string, value: unknown) => {
      mixpanel.track(MixpanelEvent.FilterColumn, {
        [MixpanelEventProperty.Filter]: filter,
        [MixpanelEventProperty.Value]: value,
        [MixpanelEventProperty.Source]: MixpanelEventSource.MultilegPriceGrid,
      });
    };

    if (!areSelectionsEqual(filterState.currencies, nextFilterState.currencies)) {
      trackSelectionFilter('Ccy', nextFilterState.currencies ?? []);
    }
    if (!areSelectionsEqual(filterState.basePairs, nextFilterState.basePairs)) {
      trackSelectionFilter('Base Pair', nextFilterState.basePairs ?? []);
    }
    if (!areSelectionsEqual(filterState.markets, nextFilterState.markets)) {
      trackSelectionFilter('Market', nextFilterState.markets ?? []);
    }
    if (!areSelectionsEqual(getSelectedMultilegTypes(filterState), getSelectedMultilegTypes(nextFilterState))) {
      trackSelectionFilter('Product', getSelectedMultilegTypes(nextFilterState));
    }

    onFilterStateChanged(nextFilterState);
  };

  const filterControls = usePriceGridFilterControls({ filterState, onFilterStateChanged: handleFilterStateChanged });

  const handleMultilegTypeToggled = (type: MultilegPriceGridType) => {
    const selectedTypesSet = new Set(selectedMultilegTypes);
    if (selectedTypesSet.has(type)) {
      selectedTypesSet.delete(type);
    } else {
      selectedTypesSet.add(type);
    }

    const nextMultilegTypes = MULTILEG_PRICE_GRID_TYPE_OPTIONS.map(option => option.type).filter(value =>
      selectedTypesSet.has(value)
    );

    handleFilterStateChanged({
      ...filterState,
      custom: {
        ...filterState.custom,
        multilegTypes: nextMultilegTypes.length === 0 ? undefined : nextMultilegTypes,
      },
    });
  };

  const handleNewMultilegClick = () => {
    mixpanel.track(MixpanelEvent.CreateNewMultileg, {
      [MixpanelEventProperty.Source]: MixpanelEventSource.MultilegPriceGrid,
    });
    navigateToMultilegCombo({ instrumentType: MultilegComboType.SyntheticCross });
  };

  return (
    <PriceGridHeaderContainer data-testid="price-grid-header">
      <PriceGridHeaderFilters>
        <Text color="colorTextImportant" fontWeight={500}>
          Multileg Price Grid
        </Text>
        <PriceGridLadderControls
          ladderType={ladderType}
          priceIncrement={priceIncrement}
          minPriceIncrement={minPriceIncrement}
          onLadderTypeChange={onLadderTypeChange}
          onPriceIncrementChange={onPriceIncrementChange}
        />
        <Divider orientation="vertical" my="spacing8" />
        <PriceGridFavoriteFilterButton filterState={filterState} onFilterStateChanged={handleFilterStateChanged} />
        <Box display="flex" alignItems="center" alignSelf="center">
          <ButtonGroup size={FormControlSizes.Small}>
            {MULTILEG_PRICE_GRID_TYPE_OPTIONS.map(({ type, label }) => {
              const selected = selectedMultilegTypes.includes(type);
              return (
                <ToggleButton
                  key={type}
                  grouped
                  selected={selected}
                  selectedVariant={ButtonVariants.Priority}
                  unselectedVariant={ButtonVariants.Default}
                  size={FormControlSizes.Small}
                  onClick={() => handleMultilegTypeToggled(type)}
                >
                  {label}
                </ToggleButton>
              );
            })}
          </ButtonGroup>
        </Box>
        <PriceGridSymbolFilters controls={filterControls} showMarkets={false} />
        <Box ref={filtersPortalRef} />
      </PriceGridHeaderFilters>
      <PriceGridHeaderActions>
        <Tooltip
          tooltip={showMultiLegSecmaster ? undefined : 'To enable Multileg trading, please contact support.'}
          placement="top-start"
        >
          <Button
            size={FormControlSizes.Small}
            variant={ButtonVariants.Primary}
            startIcon={IconName.Plus}
            onClick={handleNewMultilegClick}
            disabled={!showMultiLegSecmaster}
            data-testid="multileg-price-grid-new-multileg-button"
          >
            New Multileg
          </Button>
        </Tooltip>
        <Divider orientation="vertical" my="spacing8" />
        <PriceGridPrimingControls
          orderPresetID={orderPresetID}
          subAccount={subAccount}
          onOrderPresetChange={nextOrderPresetID =>
            handleFilterStateChanged({
              ...filterState,
              orderPresetID: nextOrderPresetID,
            })
          }
          onSubAccountChange={nextSubAccount =>
            handleFilterStateChanged({
              ...filterState,
              subAccount: nextSubAccount,
            })
          }
        />
      </PriceGridHeaderActions>
    </PriceGridHeaderContainer>
  );
}
